import axios from "axios";
import confetti from "canvas-confetti";
import { toast } from "sonner";
import { recordSample, markCrash, initRum } from "./rum";
import { getStepUpToken, requestStepUp } from "./stepup";
import { getAuthToken } from "./authToken";
import { recordNetworkFailure } from "./betaCapture";
import { emitStatusChange } from "./statusBus";
import { emitTrashChange } from "./trashBus";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, withCredentials: true });

// Streak Nudge: celebrate a member's very first library action (add/watch/favorite) —
// for a freshly-verified member this IS their first-ever app action. Backend flags it
// once via `first_add` on POST /library/status; guarded here so it can only fire once
// per page session even if multiple status calls somehow race.
let _firstActionCelebrated = false;
function celebrateFirstAction() {
  if (_firstActionCelebrated) return;
  _firstActionCelebrated = true;
  try {
    confetti({ particleCount: 140, spread: 80, origin: { y: 0.4 }, colors: ["#D4AF37", "#E8C86A", "#8B5CF6", "#ffffff"] });
    toast.success("🎬 Your first title is logged!", {
      description: "That's the start of your streak — keep tracking to build it up.",
      duration: 5000,
    });
  } catch { /* ignore */ }
}

client.interceptors.request.use(async (config) => {
  // Clerk session tokens are ~60s-lived — always fetch fresh per request
  // (Clerk's own getToken() caches/refreshes internally, so this is cheap).
  // The legacy/web session has no token to attach here anymore (2026-10-23) —
  // it rides along automatically as the httpOnly `sc_session` cookie
  // (withCredentials above); the mobile app still gets a real Bearer token
  // from its own JSON login response and attaches it on its own.
  let clerkToken = null;
  try { clerkToken = await getAuthToken(); } catch { /* ignore */ }
  if (clerkToken) config.headers.Authorization = `Bearer ${clerkToken}`;
  const gate = localStorage.getItem("sc_gate_token");
  if (gate) config.headers["X-Site-Gate"] = gate;
  const stepUp = getStepUpToken();
  if (stepUp) config.headers["X-Step-Up"] = stepUp;
  // Home Rework (2026-08): admin-only "preview as free tier" toggle. Sent on
  // every request; the backend only honors it for an actual admin account
  // (deps.get_current_user), so it's inert if a non-admin ever has it set.
  if (localStorage.getItem("sc_preview_free") === "1") config.headers["X-Preview-Free"] = "1";
  config.metadata = { start: (typeof performance !== "undefined" ? performance.now() : Date.now()) };
  return config;
});

// Record client-perceived latency for every API call (RUM Experience view).
function _rumTiming(cfg, status) {
  try {
    const url = cfg?.url || "";
    if (url.includes("/rum/beacon")) return;
    const start = cfg?.metadata?.start;
    if (start != null) {
      const now = (typeof performance !== "undefined" ? performance.now() : Date.now());
      recordSample(url, now - start, status);
    }
  } catch { /* ignore */ }
}

// If the pre-launch gate rejects us (missing/expired token), clear it and show
// the gate screen again. Never triggers once the correct passcode is stored.
client.interceptors.response.use(
  (r) => {
    _rumTiming(r.config, r.status);
    if (r.config?.url?.includes("/library/status") && r.data?.first_add) celebrateFirstAction();
    // Broadcast every successful watch-status write — even ones made outside
    // WatchlistContext (TitleDetail, LogModal, YourTakePanel, the Library
    // pages, CatchUp, etc. all POST /library/status directly) — so every
    // open popup/card elsewhere in the SPA keeps a correct baseline instead
    // of relying on a once-per-session snapshot (2026-11-02). Also covers
    // POST /library/episode (season status pills can silently re-derive
    // status) and POST /diary ("Log a Movie or Show" always marks the title
    // watched) — the write payload (tmdb_id + media_type + status) sits at
    // the top level for the first two but nested under `entry` for diary's
    // `{ok, entry}` response shape, hence checking both. POST /ratings also
    // implicitly marks a title watched (2026-11-04) — same top-level shape
    // as /library/status, so it reuses this exact check.
    if (r.config?.method === "post"
        && (r.config?.url?.includes("/library/status") || r.config?.url?.includes("/library/episode")
            || r.config?.url?.includes("/diary") || r.config?.url?.includes("/ratings"))) {
      const payload = r.data?.tmdb_id != null ? r.data : r.data?.entry;
      if (payload?.tmdb_id != null && payload?.media_type && "status" in payload) {
        emitStatusChange(`${payload.media_type}-${payload.tmdb_id}`, payload.status ?? null);
      }
    }
    // Keep the "Recently Removed" nav badge live: fires on every single-item
    // delete (DELETE /library/{type}/{id} — bulk removes are just N of these
    // in parallel) and on both restore paths (Undo toast + Trash page), so
    // the count updates immediately instead of only on next page mount.
    if ((r.config?.method === "delete" && /\/library\/[^/]+\/\d+$/.test(r.config?.url || ""))
        || (r.config?.method === "post" && /\/library\/(restore|trash\/restore)$/.test(r.config?.url || ""))) {
      emitTrashChange();
    }
    return r;
  },
  async (error) => {
    _rumTiming(error.config, error.response?.status || 0);
    // Content moderation block (HIGH severity): surface the guideline message once,
    // uniformly across every text surface, and mark it handled so callers don't
    // also show a generic "couldn't save" toast.
    if ((error.response?.status === 422 || error.response?.status === 400)
        && typeof error.response?.data?.detail === "string"
        && /community guidelines/i.test(error.response.data.detail)) {
      try { toast.error(error.response.data.detail, { duration: 6000 }); } catch { /* ignore */ }
      if (error.config) error.config.__moderationBlocked = true;
    }
    if (error.response?.status === 403 && error.response?.data?.gate_required) {
      // Only reset if we actually had a token (expired/invalid) — avoids a
      // reload loop on the very first load before the passcode is entered.
      if (localStorage.getItem("sc_gate_token")) {
        localStorage.removeItem("sc_gate_token");
        window.location.reload();
      }
    }
    // Step-up re-auth: a destructive admin action needs a fresh MFA code. Prompt
    // for one (StepUpProvider dialog), then transparently retry the request once.
    if (error.response?.status === 428 && error.response?.data?.detail === "step_up_required"
        && error.config && !error.config.__stepUpRetried) {
      try {
        await requestStepUp();
        error.config.__stepUpRetried = true;
        return client(error.config);
      } catch {
        return Promise.reject(error);
      }
    }
    // Report server 5xx and network failures (not expected 4xx like 401/403/404).
    try {
      const url = error.config?.url || "";
      const status = error.response?.status;
      const isReportCall = url.includes("/errors/report");
      if (!isReportCall && !url.includes("/beta/report")) {
        recordNetworkFailure(error.config?.method, url, status || 0);
      }
      if (!isReportCall && (!status || status >= 500)) {
        reportError(`API ${error.config?.method?.toUpperCase() || ""} ${url} failed: ${status || "network"}`.trim(), {
          route: url,
          category: "api",
          extra: { status: status || null, detail: error.response?.data?.detail },
        });
      }
    } catch { /* swallow */ }
    return Promise.reject(error);
  }
);

// Backend returns full image URLs (poster_url / backdrop_url / photo_url), so
// this is a passthrough kept for any raw path usage.
export const imgUrl = (path) => path || null;

// ---- Web telemetry ---------------------------------------------------------
// Report web-side client errors and analytics events so the admin dashboards
// capture WEB activity too (previously only mobile fed these). All best-effort
// and tagged platform:"web" so admin can split web vs mobile.
const APP_VERSION = process.env.REACT_APP_VERSION || "web";

// Capture a small JPEG screenshot of the current screen at crash time (best-effort).
// Lazy-loaded so html2canvas never affects normal page load; only used for fatal errors
// (and, on-demand, by the beta feedback widget's optional screenshot checkbox).
export async function captureScreenshot() {
  try {
    const { default: html2canvas } = await import("html2canvas");
    const canvas = await Promise.race([
      html2canvas(document.body, {
        logging: false, useCORS: true, allowTaint: true,
        scale: Math.min(0.6, (window.devicePixelRatio || 1)),
        width: Math.min(window.innerWidth, 1280),
        height: Math.min(window.innerHeight, 900),
        windowWidth: document.documentElement.clientWidth,
        windowHeight: document.documentElement.clientHeight,
      }),
      new Promise((_, rej) => setTimeout(() => rej(new Error("timeout")), 3500)),
    ]);
    return canvas.toDataURL("image/jpeg", 0.5);
  } catch {
    return null;
  }
}

export function reportError(message, { stack, route, category, fatal, extra } = {}) {
  try {
    // Never report errors caused by the reporting call itself (avoid loops).
    if (typeof message === "string" && message.includes("/errors/report")) return;
    const send = (screenshot) => client.post("/errors/report", {
      message: String(message || "Unknown error").slice(0, 600),
      stack: stack ? String(stack).slice(0, 6000) : undefined,
      route: route || window.location?.pathname,
      category: category || "frontend",
      platform: "web",
      app_version: APP_VERSION,
      fatal: !!fatal,
      extra,
      screenshot: screenshot || undefined,
    }).catch(() => {});
    // Only grab a screenshot for fatal (uncaught) errors — that's when the user
    // actually saw a broken screen; keeps overhead off routine handled errors.
    if (fatal) {
      captureScreenshot().then(send);
    } else {
      send(null);
    }
  } catch { /* swallow */ }
}

export function track(event, meta = {}) {
  try {
    client.post("/analytics/event", { event, meta, platform: "web" }).catch(() => {});
  } catch { /* swallow */ }
}

// Global handlers — uncaught errors and unhandled promise rejections.
if (typeof window !== "undefined" && !window.__sc_err_hooked) {
  window.__sc_err_hooked = true;
  window.addEventListener("error", (e) => {
    try { markCrash(); } catch { /* ignore */ }
    reportError(e?.message || "window.onerror", {
      stack: e?.error?.stack, fatal: true,
      extra: { file: e?.filename, line: e?.lineno, col: e?.colno },
    });
  });
  window.addEventListener("unhandledrejection", (e) => {
    const r = e?.reason;
    reportError(r?.message || String(r) || "unhandledrejection", { stack: r?.stack });
  });
  try { initRum(); } catch { /* ignore */ }
}

export function formatApiError(detail) {
  if (detail == null) return "Something went wrong. Please try again.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail))
    return detail
      .map((e) => (e && typeof e.msg === "string" ? e.msg : JSON.stringify(e)))
      .filter(Boolean)
      .join(" ");
  if (detail && typeof detail.msg === "string") return detail.msg;
  if (detail && detail.message) return detail.message;
  return String(detail);
}

export default client;
